import CancelButton from "components/CancelButton";
import CheckBoxLabelHookForm from "components/CheckBoxLabelHookForm";
import ConfirmBoxNoLabel from "components/ConfirmBoxNoLabel";
import CustomLoader from "components/CustomLoader";
import SubmitHookForm from "components/SubmitHookForm";
import TextInputLabelHookForm from "components/TextInputLabelHookForm";
import { accountAtom } from "modules/Login/atoms/AccountAtom";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  hierarchyFormModalState,
  hierarchyTypeModalState,
  hierarchyTypeState,
  isAddHierarchy,
  isEditHierarchy,
  rowForEditHierarchy,
} from "../atoms/atoms";
import { IHierarchyDetails } from "../types";
import SelectHookFormSingle from "components/SelectHookFormSingle";
import TextInputHookFormDisabled from "components/TextInputHookFormDisabled";
import { toast } from "components/toastNotification";
import { useGetHierarchyTypes } from "../hooks/useGetHierarchyTypes";
import useAddHierarchy from "../hooks/useAddHierarchy";
import { useQueryClient } from "react-query";
import useUpdateHierarchy from "../hooks/useUpdateHierarchy";
import useSendHierarchyEmail from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useSendHierarchyEmail";
import { useGetHierarchyEmployeeOpts } from "../hooks/useGetHierarchyEmployeeOpts";

const HierarchyForm = ({
  hierarchyData,
  isLoading,
  setFilteredHierarchies,
}) => {
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditHierarchy);

  const useFormMethods = useForm<IHierarchyDetails>({
    defaultValues: {
      ...rowForEdit,
    },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;
  const [newData, setNewData] = useState<IHierarchyDetails>();
  const [isAddState, setIsAddState] = useRecoilState(isAddHierarchy);
  const [isEditState, setIsEditState] = useRecoilState(isEditHierarchy);

  const [saveModal, setSaveModal] = useState(false);
  const userAccount = useRecoilValue(accountAtom);
  const [, setShowHierarchyTypeModal] = useRecoilState(hierarchyTypeModalState); // For show type of hierarchy modal
  const [, setShowHierarchyFormModalState] = useRecoilState(
    hierarchyFormModalState
  ); //For show form modal
  const [hierarchyType, setHierarchyType] = useRecoilState(hierarchyTypeState); //For hierarchy type value
  const queryClient = useQueryClient();
  const { mutateAsync: sendHierarchyEmail } = useSendHierarchyEmail();

  useEffect(() => {
    if (isEditState) {
      setHierarchyType(rowForEdit?.hierarchyTypeName ?? "");
    }
  }, []);

  //#region Add Hierarchy
  const {
    mutate: addHierarchy,
    isSuccess: addSuccess,
    isLoading: addLoading,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddHierarchy();

  //#endregion

  //#region Update Hierarchy
  const {
    mutate: updateHierarchy,
    isSuccess: updateSuccess,
    isLoading: updateLoading,
    isError: updateError,
    data: updateResult,
    reset: resetUpdateData,
  } = useUpdateHierarchy();
  //#endregion

  //#region Parent Hierarchy Opts
  const hierarchyTypeToFilter: Record<string, string[]> = {
    Team: ["District"],
    District: ["State"],
    State: ["Market"],
    Market: ["Business Unit"],
  };
  const parentHierarchyType = hierarchyTypeToFilter[hierarchyType] || [];
  const isSuperUser = userAccount.isFranchise && userAccount.isNonFranchise;
  const isFranchiseValue = useFormMethods.watch("isFranchise");

  const filteredParentHierarchy = hierarchyData?.filter(
    (x) => x.isSplcHierarchy === true
  );

  const parentHierarchyOpts = filteredParentHierarchy?.filter((x) => {
    if (isSuperUser) {
      if (isFranchiseValue) {
        return x.isFranchise === true;
      } else {
        return x.isFranchise !== true;
      }
    }
    return x.isFranchise === userAccount.isFranchise;
  });

  const parentHierarchy = parentHierarchyOpts
    ?.filter(
      (x) =>
        parentHierarchyType.includes(x.hierarchyTypeName) &&
        x.hierarchyName !== null
    )
    ?.map((x) => ({
      label: x.hierarchyName + " - " + x.employeeName,
      value: x.hierarchyId,
    }));
  //#endregion

  //#region Employee Opts
  const { data: employeeOptions, isLoading: empLoading } =
    useGetHierarchyEmployeeOpts();

  const empOptFranchise = employeeOptions?.filter((x) => {
    if (isSuperUser) {
      return isFranchiseValue
        ? x.employeeTypeName === "Franchise"
        : x.employeeTypeName !== "Franchise";
    }
    return userAccount.isFranchise
      ? x.employeeTypeName === "Franchise"
      : x.employeeTypeName !== "Franchise";
  });

  const employeeOpt = empOptFranchise?.map((x) => {
    return {
      label: x.employeeId + " - " + x.fullname,
      value: x.employeeId,
    };
  });
  //#endregion

  //#region Hierarchy Type Opts
  const { data: hierarchyTypes, isLoading: isHierarchyTypeLoading } =
    useGetHierarchyTypes();

  const hierarchyTypeOpts = hierarchyTypes
    ?.filter((x) => x.hierarchyTypeName !== "Team" && x.isActive === true)
    ?.map((x) => {
      return {
        label: x.hierarchyTypeName,
        value: x.hierarchyTypeId,
      };
    });
  //#endregion

  //#region OnSubmit
  const onSubmit = (data) => {
    setNewData({
      ...data,
      isFranchise:
        userAccount.isFranchise && userAccount.isNonFranchise
          ? useFormMethods.watch("isFranchise")
          : userAccount.isFranchise,
      isActive: isAddState ? true : useFormMethods.watch("isActive"),
      isSplcHierarchy: true,
    });

    if (isAddState) {
      setSaveModal(true);
    } else if (isEditState && isDirty) {
      setSaveModal(true);
    } else {
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };
  //#endregion

  //#region Handle Save
  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      addHierarchy(newData);
    } else {
      updateHierarchy(newData);
    }
  };
  //#endregion

  //#region Handle Cancel
  const handleCancel = () => {
    setSaveModal(false);
    setIsAddState(false);
    setIsEditState(false);
    setHierarchyType("");
    setShowHierarchyTypeModal(false);
    setShowHierarchyFormModalState(false);
    setRowForEdit(undefined);
  };
  //#endregion

  useEffect(() => {
    const teamId = hierarchyTypes?.find(
      (x) => x.hierarchyTypeName === hierarchyType
    )?.hierarchyTypeId;

    if (hierarchyType === "Team") {
      useFormMethods.setValue("hierarchyTypeId", teamId);
    }
  }, [useFormMethods.watch("hierarchyTypeId"), hierarchyTypes]);

  const hierarchyLabel = () => {
    const action = isEditState ? "Edit" : "Add";
    const type = hierarchyType === "Team" ? "Team" : "SPLC Hierarchy";

    return `${action} ${type}`;
  };

  //#region Add Hierarchy Status
  if (addSuccess && addResult) {
    if (addResult.data === "Duplicate") {
      toast.fire("Failed", "SPLC Hierarchy already exists.", "error");
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      sendHierarchyEmail(addResult.data, {
        onSuccess: (res) => {
          if (res) {
            if (res.status === 400) {
              toast.fire(
                "Success",
                "SPLC Hierarchy Updated Successfully, but failed in sending email",
                "warning"
              );
            } else {
              toast.fire(
                "Success",
                "SPLC Hierarchy added successfully.",
                "success"
              );
              queryClient.invalidateQueries("GetHierarchies");
              handleCancel();
              setIsAddState(false);
            }
          }
        },
      });
      toast.fire("Success", "SPLC Hierarchy added successfully.", "success");
      queryClient.invalidateQueries("GetHierarchies");
      handleCancel();
      setIsAddState(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New SPLC Hierarchy added successfully, but " +
            addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to add SPLC Hierarchy! No Data Saved.",
          "error"
        );
      }
      queryClient.invalidateQueries("GetHierarchies");
      setIsAddState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to add SPLC Hierarchy! No Data Saved.",
        "error"
      );
      queryClient.invalidateQueries("GetHierarchies");
      setIsAddState(false);
    }
    resetAddData();
    setSaveModal(false);
  }
  if (addError) {
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to add SPLC Hierarchy! No Data Saved.",
      "error"
    );
    queryClient.invalidateQueries("GetHierarchies");
    setIsAddState(false);
  }
  //#endregion

  //#region Update Hierarchy Status
  if (updateSuccess && updateResult) {
    if (updateResult.data === "Duplicate") {
      toast.fire("Failed", "SPLC Hierarchy record already exists.", "error");
      setSaveModal(false);
    } else if (
      (updateResult && updateResult.status === 201) ||
      updateResult.status === 200
    ) {
      sendHierarchyEmail(updateResult.data, {
        onSuccess: (res) => {
          if (res) {
            if (res.status === 400) {
              toast.fire(
                "Success",
                "SPLC Hierarchy updated successfully, but failed in sending email",
                "warning"
              );
            } else {
              toast.fire(
                "Success",
                "SPLC Hierarchy updated successfully",
                "success"
              );
            }
          }
        },
      });
      toast.fire("Success", "SPLC Hierarchy updated successfully.", "success");
      queryClient.invalidateQueries("GetHierarchies");
      queryClient.invalidateQueries("GetHierarchyOrgChart");
      queryClient.invalidateQueries(
        "GetParentHierarchyById",
        hierarchyData?.hierarchyId
      );
      handleCancel();
      setRowForEdit(undefined);
      setIsEditState(false);
    } else if (updateResult.status === 500) {
      if (
        updateResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        updateResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New SPLC Hierarchy updated successfully, but " +
            updateResult.data.message,
          "warning"
        );
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to update SPLC Hierarchy! No Data Saved.",
          "error"
        );
      }
      queryClient.invalidateQueries("GetHierarchies");
      setIsEditState(true);
    } else {
      toast.fire(
        "Server Error",
        "Failed to update SPLC Hierarchy! No Data Saved.",
        "error"
      );
      queryClient.invalidateQueries("GetHierarchies");
      setIsEditState(true);
    }
    resetUpdateData();
  }
  if (updateError) {
    // setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to update SPLC Hierarchy! No Data Saved.",
      "error"
    );
    queryClient.invalidateQueries("GetHierarchies");
    setIsEditState(false);
  }
  //#endregion

  return (
    <div>
      {isLoading ? (
        <div>
          <CustomLoader />
        </div>
      ) : (
        <div className="">
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-primary font-bold font-sans text-base pb-2 pr-2">
              <div className="">{hierarchyLabel()}</div>
              {saveModal ? (
                <div className="">
                  <ConfirmBoxNoLabel
                    onClick={() => handleSave()}
                    onCLose={() => setSaveModal(false)}
                  />
                </div>
              ) : (
                <div className="flex justify-between gap-2">
                  <SubmitHookForm
                    type="submit"
                    className={
                      addLoading || updateLoading
                        ? "opacity-60 cursor-not-allowed space-x-2"
                        : ""
                    }
                    buttonText={addLoading || updateLoading ? "Saving" : "Save"}
                    onClick={undefined}
                  />
                  <CancelButton
                    type="button"
                    disabled={addLoading || updateLoading ? true : false}
                    onClick={() => {
                      handleCancel();
                    }}
                  />
                </div>
              )}
            </div>
            <div className="h-0.5 w-full border-b-2 border-secondary"></div>
            <div className="grid grid-cols-2">
              <div className="text-xs text-secondary italic font-normal mt-3 mb-2">
                <b> Note:</b> All fields with * are required to fill.
              </div>
            </div>
            <div className="mt-2 bg-white px-4 pb-4 shadow-lg rounded-md">
              <div className="additional-details">
                <div className="text-primary font-bold font-sans text-sm py-2">
                  SPLC Hierarchy Details
                </div>
                <div className="grid grid-cols-2 gap-12">
                  <div className=" space-y-1 ">
                    {userAccount.isNonFranchise && userAccount.isFranchise && (
                      <CheckBoxLabelHookForm
                        name="isFranchise"
                        label="Franchise"
                        type="checkbox"
                        register={useFormMethods.register}
                        disabled={saveModal}
                        onClick={(e) => {
                          const isBusinessUnit =
                            rowForEdit?.hierarchyTypeName === "Business Unit";
                          const isChecked = e.target.checked;
                          const isFranchise = rowForEdit?.isFranchise;

                          const parentHierarchyValue =
                            useFormMethods.watch("parentHierarchyId");
                          const employeeValue =
                            useFormMethods.watch("employeeId");

                          const hasParentHierarchy = parentHierarchy?.filter(
                            (x) => x.value === parentHierarchyValue
                          );
                          const hasEmployee = employeeOpt?.filter(
                            (x) => x.value === employeeValue
                          );

                          if (!isChecked && isFranchise) {
                            useFormMethods.setValue("parentHierarchyId", null);

                            if (isBusinessUnit) {
                              useFormMethods.setValue("employeeId", null);
                            }
                          } else {
                            if (!isBusinessUnit) {
                              if (
                                isChecked &&
                                hasParentHierarchy?.length > 0 &&
                                hasEmployee?.length > 0
                              ) {
                                useFormMethods.setValue(
                                  "parentHierarchyId",
                                  null
                                );
                                useFormMethods.setValue("employeeId", null);
                              } else {
                                useFormMethods.setValue(
                                  "parentHierarchyId",
                                  rowForEdit?.parentHierarchyId ?? null
                                );
                                useFormMethods.setValue(
                                  "employeeId",
                                  rowForEdit?.employeeId ?? null
                                );
                              }
                            } else {
                              if (isChecked && hasEmployee?.length > 0) {
                                useFormMethods.setValue("employeeId", null);
                              } else {
                                useFormMethods.setValue(
                                  "employeeId",
                                  rowForEdit?.employeeId ?? null
                                );
                              }
                            }
                          }
                        }}
                      />
                    )}
                    <TextInputLabelHookForm
                      name="hierarchyName"
                      label="Hierarchy Name"
                      type="text"
                      maxLength={50}
                      className={
                        useFormMethods.formState.errors.hierarchyName &&
                        useFormMethods.formState.errors.hierarchyName.message &&
                        "border-2 border-red-500 col-span-2"
                      }
                      required={true}
                      onChange={(e) => {
                        useFormMethods.setValue(
                          "hierarchyName",
                          e ? e.target.value : null
                        );
                      }}
                      disabled={saveModal}
                      register={useFormMethods.register("hierarchyName", {
                        required: {
                          value: true,
                          message: "Required Field",
                        },
                      })}
                    />

                    {hierarchyType === "Team" && (
                      <TextInputHookFormDisabled
                        name="hierarchyTypeId"
                        label="Hierarchy Type"
                        type="text"
                        register={useFormMethods.register(
                          "hierarchyTypeId",
                          {}
                        )}
                        value={"Team"}
                      />
                    )}
                    {hierarchyType !== "Team" && (
                      <>
                        <Controller
                          control={useFormMethods.control}
                          name="hierarchyTypeId"
                          rules={{ required: true }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                                setHierarchyType(e?.label);
                                useFormMethods.setValue(
                                  "parentHierarchyId",
                                  null
                                );
                              }}
                              required={true}
                              isDisabled={
                                saveModal ||
                                hierarchyType === "Team" ||
                                isEditState
                                  ? true
                                  : false
                              }
                              className={
                                useFormMethods.formState.errors
                                  .hierarchyTypeId &&
                                "border-2 border-red-500 col-span-2"
                              }
                              isLoading={isHierarchyTypeLoading}
                              value={field.value}
                              options={hierarchyTypeOpts}
                              label="Hierarchy Type"
                            />
                          )}
                        />
                      </>
                    )}
                  </div>

                  <div className="space-y-1 px-4 ">
                    <TextInputLabelHookForm
                      name="description"
                      label="Description"
                      type="text"
                      register={useFormMethods.register("description")}
                      disabled={saveModal}
                    />
                    {useFormMethods.watch("hierarchyTypeId") !== 1 && (
                      <Controller
                        name="parentHierarchyId"
                        control={useFormMethods.control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <SelectHookFormSingle
                            field={field}
                            onChange={(e) => {
                              field.onChange(e ? e.value : null);
                            }}
                            options={parentHierarchy}
                            isDisabled={saveModal}
                            disabled={saveModal}
                            value={field.value}
                            label="Parent Hierarchy"
                            className={
                              useFormMethods.formState.errors
                                .parentHierarchyId &&
                              "border-2 border-red-500 col-span-2 "
                            }
                            required={true}
                          />
                        )}
                      />
                    )}
                    {isEditState && (
                      <CheckBoxLabelHookForm
                        name="isActive"
                        label="Is Active"
                        type="checkbox"
                        register={useFormMethods.register}
                        disabled={saveModal}
                      />
                    )}
                    <div className="space-y-1 w-full text-xs">
                      <div className="grid grid-cols-5 md:grid-cols-1  w-full items-center gap-4"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-2 bg-white px-4 pb-4 shadow-lg rounded-md">
              <div className="text-primary font-bold font-sans text-sm py-2">
                Leadership
              </div>
              <div className="grid justify-items-start grid-cols-2 gap-12">
                <div className="w-full space-y-1">
                  <div className="grid grid-cols-1 md:grid-cols-1 items-center w-full gap-2">
                    <div className="space-y-1">
                      <div className="space-y-1 w-full text-xs">
                        <div className="grid grid-cols-5 md:grid-cols-1 place-items-center  w-full items-center gap-2">
                          <Controller
                            control={useFormMethods.control}
                            name="employeeId"
                            rules={{ required: true }}
                            render={({ field }) => {
                              return (
                                <SelectHookFormSingle
                                  field={field}
                                  onChange={(e) => {
                                    field.onChange(e ? e.value : null);
                                  }}
                                  value={field.value}
                                  isDisabled={saveModal}
                                  options={employeeOpt}
                                  required={true}
                                  isLoading={empLoading}
                                  disabled={saveModal}
                                  label={(() => {
                                    const managerLabelMap: Record<
                                      string,
                                      string
                                    > = {
                                      Hierarchy: "Manager",
                                      Team: "Team Manager",
                                      District: "District Manager",
                                      State: "Director",
                                      Market: "Vice President",
                                      "Business Unit": "Senior Vice President",
                                    };

                                    return managerLabelMap[
                                      hierarchyType ?? "Hierarchy"
                                    ];
                                  })()}
                                  className={
                                    useFormMethods.formState.errors
                                      .employeeId &&
                                    "border-2 border-red-500 col-span-2"
                                  }
                                />
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <TextInputLabelHookForm
                          name="managerEffectiveDate"
                          label="Effective Date"
                          type="date"
                          register={useFormMethods.register(
                            "managerEffectiveDate",
                            {
                              required: {
                                value: true,
                                message: "Required Field",
                              },
                            }
                          )}
                          className={
                            useFormMethods.formState.errors
                              .managerEffectiveDate &&
                            useFormMethods.formState.errors.managerEffectiveDate
                              .message &&
                            "border-2 border-red-500 col-span-2"
                          }
                          required={true}
                          disabled={saveModal}
                        />
                      </div>
                      <div>
                        {isEditState && (
                          <TextInputLabelHookForm
                            name="previousManagerName"
                            label="Previous Manager"
                            type="text"
                            placeholder=""
                            register={useFormMethods.register(
                              "previousManagerName"
                            )}
                            disabled={true}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default HierarchyForm;
